// Small display components: Avatar, CopyButton, Thumbnail, KeyValueList, Stat + Sparkline,
// Timeline, Stepper.
import { Fragment, useId, useMemo, useState, type ReactNode } from 'react'
import * as A from '@radix-ui/react-avatar'
import { ArrowDownRight, ArrowUpRight, Check, Copy, ImageOff, Minus } from 'lucide-react'
import { cn } from './cn'
import { Tooltip } from './tooltip'
import { initials, percent } from '@/lib/format'
import { toast } from './toast'

// ─── Avatar ─────────────────────────────────────────────────────────────────────────────

const AVATAR_HUES = [262, 200, 160, 24, 330, 290, 190, 45, 130, 350]
function hueFor(s: string) { let h = 0; for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0; return AVATAR_HUES[h % AVATAR_HUES.length] }

/** Person or store avatar with image + deterministic-colour initials fallback. `square` for stores. */
export function Avatar({ name, src, size = 28, square, className }: { name: string | null | undefined; src?: string | null; size?: number; square?: boolean; className?: string }) {
  const label = name || '?'
  const hue = hueFor(label)
  return (
    <A.Root className={cn('relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden', square ? 'rounded-[28%]' : 'rounded-full', className)}
      style={{ width: size, height: size }}>
      {src && <A.Image src={src} alt="" className="size-full object-cover" />}
      <A.Fallback delayMs={src ? 400 : 0}
        className="flex size-full items-center justify-center font-semibold text-white"
        style={{ fontSize: Math.max(9, Math.round(size * 0.38)), background: `linear-gradient(145deg, oklch(0.66 0.13 ${hue}), oklch(0.52 0.15 ${hue + 18}))` }}>
        {initials(label)}
      </A.Fallback>
    </A.Root>
  )
}

// ─── CopyButton ─────────────────────────────────────────────────────────────────────────

/** Copies text with a check-mark confirmation. `<CopyButton value={order.id} label="Copy order ID" />` */
export function CopyButton({ value, label = 'Copy', size = 'sm', className, children }: { value: string; label?: string; size?: 'xs' | 'sm'; className?: string; children?: ReactNode }) {
  const [done, setDone] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Fallback for insecure contexts / denied permission.
      const ta = document.createElement('textarea')
      ta.value = value; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select()
      const ok = document.execCommand('copy'); ta.remove()
      if (!ok) { toast.error('Couldn’t copy — select the text and copy it manually.'); return }
    }
    setDone(true)
    setTimeout(() => setDone(false), 1400)
  }
  const btn = (
    <button type="button" onClick={copy} aria-label={done ? 'Copied' : label}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-md text-text-subtle transition-colors hover:bg-surface-active hover:text-text focus-visible:outline-2 focus-visible:outline-focus',
        children ? 'h-7 px-2 text-sm font-medium' : size === 'xs' ? 'size-5 justify-center' : 'size-7 justify-center',
        done && 'text-success-strong hover:text-success-strong', className,
      )}>
      {done ? <Check className={size === 'xs' ? 'size-3' : 'size-3.5'} /> : <Copy className={size === 'xs' ? 'size-3' : 'size-3.5'} />}
      {children}
    </button>
  )
  return children ? btn : <Tooltip content={done ? 'Copied' : label}>{btn}</Tooltip>
}

// ─── Thumbnail ──────────────────────────────────────────────────────────────────────────

const thumbSizes = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 } as const

/** Product/media thumbnail: lazy, object-cover, graceful fallback icon when missing or broken. */
export function Thumbnail({ src, alt = '', size = 'md', srcSet, className, fallback }: { src?: string | null; alt?: string; size?: keyof typeof thumbSizes | number; srcSet?: string; className?: string; fallback?: ReactNode }) {
  const [broken, setBroken] = useState(false)
  const px = typeof size === 'number' ? size : thumbSizes[size]
  return (
    <span className={cn('relative inline-grid shrink-0 place-items-center overflow-hidden rounded-[8px] border border-border bg-surface-sunken text-text-subtle', className)}
      style={{ width: px, height: px, borderRadius: px <= 32 ? 6 : 8 }}>
      {src && !broken
        ? <img src={src} srcSet={srcSet} sizes={`${px}px`} alt={alt} loading="lazy" decoding="async" onError={() => setBroken(true)} className="size-full object-cover" />
        : (fallback ?? <ImageOff aria-hidden style={{ width: Math.max(12, px * 0.4), height: Math.max(12, px * 0.4) }} strokeWidth={1.5} />)}
    </span>
  )
}

// ─── KeyValueList ───────────────────────────────────────────────────────────────────────

export interface KeyValueItem { label: ReactNode; value: ReactNode; copy?: string; hidden?: boolean }

/**
 * Definition list for details: `<KeyValueList items={[{label:'Email', value:c.email, copy:c.email}]} />`.
 * `layout="stacked"` puts labels above values (narrow sidebars).
 */
export function KeyValueList({ items, layout = 'inline', className }: { items: KeyValueItem[]; layout?: 'inline' | 'stacked'; className?: string }) {
  const shown = items.filter(i => !i.hidden)
  return (
    <dl className={cn(layout === 'inline' ? 'grid grid-cols-[minmax(96px,auto)_minmax(0,1fr)] gap-x-6 gap-y-3' : 'flex flex-col gap-3', className)}>
      {shown.map((it, i) => (
        <Fragment key={i}>
          <dt className={cn('text-base text-text-muted', layout === 'stacked' && 'text-sm')}>{it.label}</dt>
          <dd className={cn('group flex min-w-0 items-center gap-1 text-base text-text', layout === 'stacked' && '-mt-2')}>
            <span className="min-w-0 break-words">{it.value ?? <span className="text-text-subtle">—</span>}</span>
            {it.copy && <CopyButton value={it.copy} size="xs" className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100" />}
          </dd>
        </Fragment>
      ))}
    </dl>
  )
}

// ─── Stat + Sparkline ───────────────────────────────────────────────────────────────────

/**
 * Tiny SVG trend line (no axes). Full width of its container by default (pass `width` for a
 * fixed size). Colour follows `tone`; hidden from screen readers.
 */
export function Sparkline({ data, width, height = 32, tone = 'primary', className, fill = true }: { data: number[]; width?: number; height?: number; tone?: 'primary' | 'success' | 'danger' | 'neutral'; className?: string; fill?: boolean }) {
  const W = 100
  const { line, area } = useMemo(() => {
    if (data.length < 2) return { line: '', area: '' }
    const min = Math.min(...data), max = Math.max(...data)
    const span = max - min || 1
    const pts = data.map((v, i) => [(i / (data.length - 1)) * W, height - 2 - ((v - min) / span) * (height - 4)] as const)
    const l = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
    return { line: l, area: `${l} L${W},${height} L0,${height} Z` }
  }, [data, height])
  const color = { primary: 'var(--primary)', success: 'var(--success)', danger: 'var(--danger)', neutral: 'var(--text-subtle)' }[tone]
  const gid = useId().replace(/:/g, '')
  if (!line) return <svg width={width ?? '100%'} height={height} className={className} aria-hidden />
  return (
    <svg width={width ?? '100%'} height={height} viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" className={cn('block overflow-visible', className)} aria-hidden>
      {fill && (
        <>
          <defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.18" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
          <path d={area} fill={`url(#${gid})`} />
        </>
      )}
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export interface StatProps {
  label: ReactNode
  /** Pre-formatted value (use money()/number()). */
  value: ReactNode
  /** Change vs previous period as a ratio (0.12 = +12%). */
  delta?: number | null
  /** Label for the comparison, e.g. "vs last 30 days". */
  deltaLabel?: ReactNode
  /** When lower is better (refund rate, cost), a decrease shows green. */
  invertDelta?: boolean
  /** Sparkline or any chart. `<Sparkline data={…}/>` */
  chart?: ReactNode
  loading?: boolean
  /** Makes the whole tile a link/button target. */
  onClick?: () => void
  help?: ReactNode
  className?: string
}

/** KPI tile: `<Stat label="Total sales" value={money(t)} delta={0.12} deltaLabel="vs last 30 days" chart={<Sparkline data={pts}/>} />` */
export function Stat({ label, value, delta, deltaLabel, invertDelta, chart, loading, onClick, help, className }: StatProps) {
  const good = delta === null || delta === undefined || delta === 0 ? null : (delta > 0) !== !!invertDelta
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={cn('flex min-w-0 flex-col gap-2 rounded-card border border-border bg-surface p-4 text-left shadow-card',
        onClick && 'transition-colors hover:border-border-strong hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-focus', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-text-muted">{help ? <Tooltip content={help}><span className="cursor-help border-b border-dotted border-text-subtle">{label}</span></Tooltip> : label}</span>
      </div>
      {loading ? (
        <div className="flex flex-col gap-2" aria-hidden><div className="h-7 w-24 animate-shimmer rounded-md bg-surface-active" /><div className="h-3 w-20 animate-shimmer rounded bg-surface-active" /></div>
      ) : (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="min-w-0 truncate text-2xl font-semibold tracking-[-0.01em] text-text tabular" title={typeof value === 'string' ? value : undefined}>{value}</span>
          {(delta !== undefined || deltaLabel) && (
            <span className="flex min-w-0 items-center gap-2 text-sm">
              {delta !== undefined && delta !== null && (
                <span className={cn('inline-flex shrink-0 items-center gap-1 font-medium tabular', good === null ? 'text-text-muted' : good ? 'text-success-strong' : 'text-danger-strong')}>
                  {delta === 0 ? <Minus aria-hidden className="size-3.5" /> : delta > 0 ? <ArrowUpRight aria-hidden className="size-3.5" /> : <ArrowDownRight aria-hidden className="size-3.5" />}
                  {/* Direction in words for screen readers; the visible text carries the sign too, so neither colour nor icon is the only cue. */}
                  <span className="sr-only">{delta === 0 ? 'No change' : delta > 0 ? 'Up' : 'Down'}</span>
                  {Math.abs(delta) >= 10 ? `${delta > 0 ? '>' : '<'}${percent(delta > 0 ? 9.99 : -9.99, { signed: true, digits: 0 })}` : percent(delta, { signed: true })}
                </span>
              )}
              {deltaLabel && <span className="truncate text-text-subtle">{deltaLabel}</span>}
            </span>
          )}
          {chart && <div className="mt-2 min-w-0">{chart}</div>}
        </div>
      )}
    </Tag>
  )
}

// ─── Timeline ───────────────────────────────────────────────────────────────────────────

export interface TimelineItem {
  id: string
  title: ReactNode
  body?: ReactNode
  /** Pre-formatted time (relativeTime/dateTime), shown on the right. */
  time?: ReactNode
  icon?: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary'
  /** Author line for comments. */
  author?: ReactNode
}

/**
 * Vertical event history (order timeline, audit). Put a comment composer in `header`.
 * `<Timeline items={events.map(e => ({ id: e.id, title: e.message, time: relativeTime(e.createdAt) }))} />`
 */
export function Timeline({ items, header, className, emptyText = 'No activity yet.' }: { items: TimelineItem[]; header?: ReactNode; className?: string; emptyText?: ReactNode }) {
  const toneCls = { neutral: 'bg-surface-active text-text-muted', success: 'bg-success-soft text-success-strong', warning: 'bg-warning-soft text-warning-strong', danger: 'bg-danger-soft text-danger-strong', info: 'bg-info-soft text-info-strong', primary: 'bg-primary-soft text-primary-strong' }
  return (
    <div className={cn('flex flex-col', className)}>
      {header && <div className="mb-4">{header}</div>}
      {items.length === 0 ? <p className="text-base text-text-subtle">{emptyText}</p> : (
        <ol className="relative flex flex-col">
          {items.map((it, i) => (
            <li key={it.id} className="relative flex gap-3 pb-5 last:pb-0">
              {i < items.length - 1 && <span aria-hidden className="absolute top-6 bottom-0 left-[11px] w-px bg-border" />}
              <span className={cn('relative z-[1] grid size-6 shrink-0 place-items-center rounded-full ring-4 ring-surface [&_svg]:size-3', toneCls[it.tone ?? 'neutral'])}>
                {it.icon ?? <span className="size-1.5 rounded-full bg-current" />}
              </span>
              <div className="flex min-h-6 min-w-0 flex-1 flex-col justify-center gap-1">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0 text-base text-text">{it.author && <span className="mr-1 font-semibold">{it.author}</span>}{it.title}</div>
                  {it.time && <span className="shrink-0 text-sm text-text-subtle tabular">{it.time}</span>}
                </div>
                {it.body && <div className="text-base text-text-muted">{it.body}</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ─── Stepper ────────────────────────────────────────────────────────────────────────────

export interface StepperStep { id: string; label: ReactNode; description?: ReactNode }

/**
 * Progress through a multi-step flow (onboarding, import). `current` is the active index;
 * steps before it show as complete. `onStepClick` makes completed steps navigable.
 */
export function Stepper({ steps, current, orientation = 'horizontal', onStepClick, className }: { steps: StepperStep[]; current: number; orientation?: 'horizontal' | 'vertical'; onStepClick?: (index: number) => void; className?: string }) {
  return (
    <ol className={cn(orientation === 'horizontal' ? 'flex items-start gap-2' : 'flex flex-col', className)} aria-label="Progress">
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo'
        const clickable = !!onStepClick && state === 'done'
        const dot = (
          <span className={cn('grid size-6 shrink-0 place-items-center rounded-full border text-xs font-semibold tabular transition-colors duration-150',
            state === 'done' && 'border-primary bg-primary text-primary-foreground',
            state === 'current' && 'border-primary bg-primary-soft text-primary-strong ring-4 ring-primary/12',
            state === 'todo' && 'border-border-strong bg-surface text-text-subtle')}>
            {state === 'done' ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
          </span>
        )
        const text = (
          <span className="flex min-w-0 flex-col">
            <span className={cn('text-sm font-medium', state === 'todo' ? 'text-text-subtle' : 'text-text')}>{s.label}</span>
            {s.description && <span className="text-sm text-text-muted">{s.description}</span>}
          </span>
        )
        if (orientation === 'vertical') {
          return (
            <li key={s.id} className="relative flex gap-3 pb-6 last:pb-0" aria-current={state === 'current' ? 'step' : undefined}>
              {i < steps.length - 1 && <span aria-hidden className={cn('absolute top-7 bottom-1 left-[11px] w-px', i < current ? 'bg-primary' : 'bg-border')} />}
              {clickable ? <button type="button" onClick={() => onStepClick!(i)} className="flex gap-3 text-left">{dot}{text}</button> : <>{dot}{text}</>}
            </li>
          )
        }
        return (
          <li key={s.id} className="flex min-w-0 flex-1 flex-col gap-2" aria-current={state === 'current' ? 'step' : undefined}>
            <div className="flex items-center gap-2">
              {clickable ? <button type="button" onClick={() => onStepClick!(i)} aria-label={`Back to step ${i + 1}`}>{dot}</button> : dot}
              {i < steps.length - 1 && <span aria-hidden className={cn('h-px flex-1 transition-colors duration-180', i < current ? 'bg-primary' : 'bg-border')} />}
            </div>
            <div className="hidden pr-2 sm:block">{text}</div>
          </li>
        )
      })}
    </ol>
  )
}
